import { RESOLVER_CONFLITO_LABEL } from "@/shared/components/gantt-carga/gantt-carga.constants";
import type { MenuItem } from "primereact/menuitem";

import { GANTT_LAYOUT_PRESETS } from "@/ds";
import type { MarcasSimulacao } from "@/modules/recursos/simulacao";
import { Gantt, lanesMinHeightRem } from "@/shared/components/gantt";
import type { GanttColumn, GanttRow } from "@/shared/components/gantt";

import type { LocalRowModel } from "@/shared/components/gantt-carga/gantt-local-rows";
import { ConflictBand } from "@/shared/components/conflict-band";
import { RotuloObra } from "@/shared/components/rotulo-obra";
import { AllocationBar } from "@/shared/components/gantt-allocation-bar";
import { LocalConflictBadge } from "../local-conflict-badge";
import { SIMULACAO_MARCA_CLASSES, SimulacaoMarcaTag } from "@/shared/components/simulacao-marca";
import {
  LANE_PADDING_IGNORED_REM,
  LANE_PADDING_REM,
  LAYOUT_IGNORED,
  LAYOUT_PRESET,
} from "./gantt-local.constants";
import type { GanttLocalViewModel } from "./types-gantt-local";
import baseStyles from "@/shared/components/gantt-carga/gantt.module.scss";
import styles from "./gantt-local.module.scss";

function toRow(
  row: LocalRowModel,
  rowMinHeightRem: number,
  marcaDaLinha: GanttLocalViewModel["marcaDaLinha"],
  rotuloConflito: GanttLocalViewModel["rotuloConflito"],
  marcas: MarcasSimulacao,
  onResolver?: (row: LocalRowModel) => void,
): GanttRow {
  const marcaAtual = marcaDaLinha(row);
  const hasIgnored = row.bands.some((band) => band.tone === "tratado");
  const layout = hasIgnored ? { ...LAYOUT_IGNORED } : LAYOUT_PRESET;
  // "Resolver Conflito" só existe com conflito em aberto.
  const actions: MenuItem[] | undefined =
    row.conflicts > 0 && onResolver
      ? [
          {
            label: RESOLVER_CONFLITO_LABEL,
            icon: "pi pi-thumbs-up",
            command: () => onResolver(row),
          },
        ]
      : undefined;

  return {
    key: row.key,
    testId: `gantt-row-${row.kind}`,
    className: marcaAtual ? SIMULACAO_MARCA_CLASSES.linha : undefined,
    minHeight: `${lanesMinHeightRem({
      lanes: row.lanes,
      laneHeightRem: hasIgnored
        ? LAYOUT_IGNORED.laneHeightRem
        : GANTT_LAYOUT_PRESETS[LAYOUT_PRESET].laneHeightRem,
      paddingRem: hasIgnored ? LANE_PADDING_IGNORED_REM : LANE_PADDING_REM,
      minRem: rowMinHeightRem,
    })}rem`,
    actionLabel: actions ? `Ações de ${row.title}` : `Detalhes de ${row.title}`,
    actions,
    cells: [
      <div key="copy" className={baseStyles.leftCopy}>
        <span className={baseStyles.rowTitle}>{row.title}</span>
        {row.meta || row.conflicts > 0 ? (
          <span className={baseStyles.rowMeta}>
            {row.meta}
            {row.conflicts > 0 ? (
              <LocalConflictBadge label={rotuloConflito(row.conflicts)} count={row.conflicts} />
            ) : null}
          </span>
        ) : null}
        {marcaAtual ? <SimulacaoMarcaTag marca={marcaAtual} /> : null}
      </div>,
    ],
    content: (
      <>
        {row.bands.map((band) => (
          <ConflictBand
            key={band.key}
            groups={[band.group]}
            tratado={band.tone === "tratado"}
            left={band.left}
            width={band.width}
            className={
              band.tone === "tratado" ? baseStyles.conflictBandIgnored : baseStyles.conflictBand
            }
          />
        ))}
        {row.items.map((item) => (
          <AllocationBar
            key={item.key}
            item={item}
            layout={layout}
            marca={marcas.get(item.key)}
            marcaPreenchida
          />
        ))}
        {/* Obras sem Local não têm período: aparecem como pílulas, sem posição no eixo. */}
        {row.chips.length > 0 ? (
          <div className={styles.chips}>
            {row.chips.map((chip) => (
              <span
                key={chip.key}
                className={`${styles.chip} ${chip.cancelada ? styles.chipCancelada : styles.chipObra}`}
                data-testid={chip.cancelada ? "gantt-chip-cancelada" : "gantt-chip"}
                title={chip.label}
              >
                {chip.rotulo ? <RotuloObra {...chip.rotulo} /> : chip.label}
              </span>
            ))}
          </div>
        ) : null}
      </>
    ),
  };
}

/** Gantt da perspectiva Local agrupada por Local: uma linha por Local, barras = obras alocadas. */
export function GanttLocalView({
  rows,
  onResolver,
  today,
  marcas,
  marcaDaLinha,
  rotuloConflito,
  scale,
  rowMinHeightRem,
  unitMinWidth,
}: GanttLocalViewModel) {
  const columns: GanttColumn[] = [
    {
      key: "local",
      header: "Local",
      width: "var(--gantt-left)",
      cellClassName: baseStyles.leftCell,
    },
  ];

  return (
    <Gantt.Root
      className={baseStyles.root}
      corpoRola
      scale={scale}
      today={today}
      columns={columns}
      rows={rows.map((row) =>
        toRow(row, rowMinHeightRem, marcaDaLinha, rotuloConflito, marcas, onResolver),
      )}
      withActions
      grid="none"
      unitMinWidth={unitMinWidth}
    />
  );
}
